import { EditableTranscription } from '@/components/EditableTranscription';
import { LanguageSelector } from '@/components/LanguageSelector';
import { Header, Sidebar } from '@/components/layout';
import { AudioUpload } from '@/components/session/AudioUpload';
import { ModeSwitcher } from '@/components/session/ModeSwitcher';
import { PatientSearchSelect } from '@/components/session/PatientSearchSelect';
import { PipelineStallBanner } from '@/components/session/PipelineStallBanner';
import { RecordingControls } from '@/components/session/RecordingControls';
import { RecordingEmptyState } from '@/components/session/RecordingEmptyState';
import { RecordingSilenceBanner } from '@/components/session/RecordingSilenceBanner';
import { SessionBottomBar } from '@/components/session/SessionBottomBar';
import { MobileAssistantShell, MobileSessionNavigation, SessionViewport } from '@/components/session/SessionPageLayout';
import { sessionLayoutClasses } from '@/components/session/SessionPageLayout.styles';
import { shouldShowCompactRecordingControl } from '@/components/session/recordingControlVisibility';
import { SmartPanel } from '@/components/session/smart-panel';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { hasBetaAccess, hasVerkefniAccess, useAuth } from '@/contexts/AuthContext';
import { useRecordingState } from '@/contexts/RecordingContext';
import { toOrderPatient } from '@/features/blood-tests/resolveOrderPatient';
import { useGenerateSummary } from '@/hooks/useGenerateSummary';
import { useNoteInstructions } from '@/hooks/useNoteInstructions';
import { useSession } from '@/hooks/useSession';
import { useSummaryGeneration } from '@/hooks/useSummaryGeneration';
import { useTasks } from '@/hooks/useTasks';
import { useTemplateDefaults } from '@/hooks/useTemplateDefaults';
import { useSaveUserSummary } from '@/lib/api';
import {
  type PendingSegment,
  applyPhase2Refinement,
  createOnceGuard,
  invalidatePendingPositions,
  isNonSpeechMarker,
  shiftPendingSegments,
  shouldApplyDictationSmartReview,
} from '@/lib/liveTranscript';
import type { Document } from '@/lib/mockData';
import { uploadCaptureAudio } from '@/lib/recordingAudio';
import {
  type SmartReviewCompleteEvent,
  StreamingClient,
  type StreamingWarningEvent,
  type TranscriptionResult,
  type TranscriptionStatusEvent,
  type TranscriptionUpdateEvent,
} from '@/lib/streamingClient';
import { getStreamingWarningMessage } from '@/lib/streamingWarnings';
import { summaryEchoForDraft, summaryEchoForLoadedSession } from '@/lib/summaryGeneration';
import { pcmToWav } from '@/lib/wavUtils';
import { textFromSegments } from '@/pages/conversationRestore';
import { MicrophoneNotice } from '@careflux/primitives/microphone';
import { Loader2, Plus, Save, Settings } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'wouter';

import { TutorialAnchor } from '@/features/session-tutorial/TutorialAnchor';
import type { SessionTutorialDemo } from '@/features/session-tutorial/sessionTutorial.types';
import { appendDemoText, withoutDemoText } from '@/features/session-tutorial/sessionTutorialDemoContent';
import { useTutorialDemoNote } from '@/features/session-tutorial/useTutorialDemoNote';
import {
  useAudioDevices,
  useLanguages,
  usePanelResize,
  usePatientSearch,
  useRecordingTimer,
  useSettings,
  useStreamingRecorder,
  useToast,
} from '@/hooks';
import { formatTime } from '@/lib/sessionUtils';
import type { LiveSessionData, TranscriptionSegment } from '@/types/session';
import type { SummaryStyle } from '@/types/summaryStyle';
import { useDictationRecordingSignal } from './dictationRecordingSignal';

interface SessionProps {
  sessionId?: string;
}

export default function Session({ sessionId: resumeSessionId }: SessionProps = {}) {
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const { user, isViewingAsDelegate, viewingAsAuthAccountId } = useAuth();
  // The composer panel's column layout is beta-only, like the panel itself.
  const layoutClasses = sessionLayoutClasses[hasBetaAccess(user) ? 'beta' : 'legacy'];
  const { setRecording, setUploading } = useRecordingState();

  // Use custom hooks for shared functionality
  const { devices, selectedDeviceId, setSelectedDeviceId, microphone, retryMicrophone } = useAudioDevices();
  const { languages, selectedLanguage, setSelectedLanguage, selectedSummaryLanguage, setSelectedSummaryLanguage } =
    useLanguages();
  const { enableRuleBasedNormalization, asrModelPreference, streamingDictation } = useSettings();
  const { rightPanelWidth, isResizing: _isResizing, handleMouseDown } = usePanelResize('smartPanelWidth');
  const patientSearch = usePatientSearch({ resumeSessionId, loadSavedPatient: true });
  const { selectedPatient, selectedPatientRef, clearSelectedPatient, attachPatientDbId } = patientSearch;
  const bloodTestPatient = toOrderPatient(selectedPatient);

  // Recording state
  const [isRecording, setIsRecording] = useState(false);
  const [isInitializing, setIsInitializing] = useState(false); // True while connecting/setting up
  const [isPaused, setIsPaused] = useState(false); // True when recording is paused (audio capture stopped but session open)

  useDictationRecordingSignal(isRecording, setRecording);

  // Use timer hook - timer stops when paused
  const { elapsedTime, setElapsedTime } = useRecordingTimer(isRecording, isInitializing, isPaused);
  const [mobileView, setMobileView] = useState<'recording' | 'assistant'>('recording');
  const [summary, setSummary] = useState('');
  const [documents, _setDocuments] = useState<Document[]>([]);
  const [isProcessing, _setIsProcessing] = useState(false);
  const [dictationText, setDictationText] = useState('');
  const [provisionalText, setProvisionalText] = useState('');
  // Ephemeral pre-encounter context typed by the clinician (PMH, meds,
  // complaints). Stored in sessionStorage by SamhengiPanel; mirrored here as
  // a ref so handleGenerateSummary can include the freshest value without
  // re-renders driving the request fn identity. The setter is wrapped in
  // useCallback so SamhengiPanel's `setAndNotify` memo stays stable across
  // parent rerenders.
  const preEncounterContextRef = useRef<string>('');
  const handlePreEncounterContextChange = useCallback((text: string) => {
    preEncounterContextRef.current = text;
  }, []);
  const [isDictating, setIsDictating] = useState(false);
  const [_transcriptionStatus, setTranscriptionStatus] = useState<'listening' | 'transcribing' | 'idle'>('idle');
  const [_accumulatedDuration, setAccumulatedDuration] = useState(0);
  const [_targetDuration, setTargetDuration] = useState(5);
  const cursorPositionRef = useRef<number>(0);
  const selectionRangeRef = useRef<{ start: number; end: number }>({ start: 0, end: 0 });
  const editorRef = useRef<HTMLDivElement>(null); // Ref to access editor DOM for reading current text

  // Insertion mode: where new dictation text goes when resuming
  // 'append' = end of text (default), 'insert' = at cursor, 'replace' = replace selection
  const [insertionMode, setInsertionMode] = useState<'append' | 'insert' | 'replace'>('append');
  const insertionModeRef = useRef<'append' | 'insert' | 'replace'>('append'); // Ref mirror for use in closures
  const insertionPointRef = useRef<{ start: number; end: number }>({ start: 0, end: 0 });
  const insertionStartRef = useRef<number>(0); // Original cursor position when recording started
  const textAfterCursorRef = useRef<string>(''); // Text that was after cursor when recording started
  const hasReplacedSelection = useRef(false); // Track if first result already replaced selection
  const [targetCursorPosition, setTargetCursorPosition] = useState<number | null>(null);
  const [_lastASRTextLength, _setLastASRTextLength] = useState(0);
  const [_showASRHighlight, _setShowASRHighlight] = useState(false);
  const [recordedAudioUrl, setRecordedAudioUrl] = useState<string | null>(null);

  // Finalization state - when waiting for final transcription after stopping
  const [isFinalizingTranscription, setIsFinalizingTranscription] = useState(false);

  // Smart review state - when AI is reviewing/cleaning up the transcript
  const [isSmartReviewing, setIsSmartReviewing] = useState(false);

  // Pending refinement state - tracks segments waiting for large model confirmation
  // Store as array of {id, text} for display in UI
  const [pendingRefinements, setPendingRefinements] = useState<Array<{ id: string; text: string }>>([]);
  const pendingSegmentsRef = useRef<Map<string, PendingSegment>>(new Map());

  // Raw transcript state (for admin debugging)

  // Session resume state
  const [isLoadingSession, setIsLoadingSession] = useState(!!resumeSessionId);
  const [isResumedSession, setIsResumedSession] = useState(false);

  // Clinic settings state - controls whether audio is uploaded to blob storage
  // Default to false (fail-closed) until we fetch the actual clinic setting
  const [storeAudioInBlob, setStoreAudioInBlob] = useState(false);

  // Template state — the server-persisted default seeds the selection until
  // the user makes an explicit pick this session.
  const { dictation: defaultDictationTemplateId, setTemplateDefault } = useTemplateDefaults();
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null);
  const hasPickedTemplateRef = useRef(false);

  useEffect(() => {
    if (hasPickedTemplateRef.current) return;
    if (defaultDictationTemplateId != null) {
      setSelectedTemplateId(defaultDictationTemplateId);
    }
  }, [defaultDictationTemplateId]);

  // Restoring a session's saved template counts as a pick: the server default
  // must not overwrite it if the defaults query resolves later.
  const restoreTemplateSelection = (templateId: number) => {
    hasPickedTemplateRef.current = true;
    setSelectedTemplateId(templateId);
  };

  // Summary editing state
  const [editedSummary, setEditedSummary] = useState<string | null>(null);
  const saveSummaryMutation = useSaveUserSummary();
  const copySummaryMutation = useSaveUserSummary();
  const summaryGeneration = useSummaryGeneration();
  const isSavingSummary = saveSummaryMutation.isPending;
  const { mutateAsync: requestSummary, isPending: isGeneratingSummary } = useGenerateSummary();

  // Transcript editing state (dirty tracking)
  const [isTranscriptModified, setIsTranscriptModified] = useState(false);
  const [isSavingTranscript, setIsSavingTranscript] = useState(false);
  const originalTranscriptRef = useRef<string>('');
  const lastASRTranscriptRef = useRef<string>(''); // Track the last ASR-generated text to distinguish user edits
  const pauseAbortRef = useRef(false); // Guard against pause cleanup running after resume
  const isDictatingRef = useRef(false); // Ref mirror for isDictating (stable in callbacks)
  const isPausedRef = useRef(false); // Ref mirror for isPaused (stable in callbacks)

  // Mic capture + stall recovery, silence auto-pause, connection toasts, wake lock
  const {
    pcmAudioManagerRef,
    streamingClientRef,
    startCapture,
    pauseCapture,
    resumeCapture,
    connectionState,
    handleConnectionStateChange,
    audioQuality,
    pipelineStalled,
    handleManualRecovery,
    silence: { phase: silencePhase, secondsRemaining: silenceSecondsRemaining, cancel: cancelSilenceCountdown },
  } = useStreamingRecorder({
    label: 'Dictation',
    isRecording,
    isPaused,
    onAutoStop: () => pauseDictation(),
    onSessionEnded: () => {
      setIsRecording(false);
      setIsPaused(false);
      setIsInitializing(false);
    },
    onMicrophoneError: microphone.reportError,
    onMicrophoneReady: microphone.markGranted,
  });
  // Session lifecycle (mint id + navigate + POST /api/sessions) is owned by the
  // shared hook; sessionIdRef is its ref so existing reads keep working.
  const { sessionIdRef, ensureSession } = useSession({
    mode: 'dictation',
    language: selectedLanguage,
    getPatient: () => selectedPatientRef.current ?? undefined,
    onPatientResolved: attachPatientDbId,
    resumeSessionId,
  });
  const savedSegmentsRef = useRef<Set<string>>(new Set()); // Track saved segments to prevent duplicates
  const textLengthAtRecordingStartRef = useRef<number>(0); // Track text length at start of recording

  // Task detection (shared hook — DB load, server events, completion toggle)
  const { tasks, confirmedTasks, toggleCompletion, handleServerTasks, refreshTasks, updateTaskContent } = useTasks({
    sessionId: sessionIdRef.current || undefined,
    enabled: hasVerkefniAccess(user),
  });

  // Note: Removed Web Speech API - now using ASR service for instant feedback
  // This eliminates microphone conflicts and provides better Icelandic transcription

  // Handle transcription result from WebSocket (both provisional and final)
  const handleTranscription = (result: TranscriptionResult) => {
    try {
      // Check for newline-only text BEFORE trimming
      // This happens when spoken commands like "greinaskil ný lína" are normalized to "\n\n"
      const isNewlineOnly = result.text && /^[\n\s]+$/.test(result.text) && result.text.includes('\n');

      // Check for text that STARTS with newlines (paragraph break before content)
      // e.g., "\n\nSkoðun:" should preserve the leading newlines
      const hasLeadingNewlines = result.text && /^\n+/.test(result.text) && !isNewlineOnly;
      const leadingNewlines = hasLeadingNewlines ? (result.text.match(/^\n+/) || [''])[0] : '';

      const asrText = result.text.trim();
      if (!asrText && !isNewlineOnly) {
        return; // Skip empty results (unless they contain newlines)
      }
      if (isNonSpeechMarker(asrText)) {
        return; // Skip non-speech sounds
      }

      if (result.is_provisional) {
        setProvisionalText(asrText);
      } else {
        // Final or pending-refinement result
        const isPending = result.is_pending_refinement === true;

        // Handle newline-only text specially - append newlines but don't save as segment
        if (isNewlineOnly) {
          setDictationText((prev) => {
            // Append the newlines (without extra space)
            const newText = prev.trimEnd() + result.text;
            cursorPositionRef.current = newText.length;
            // Track this as the last ASR-generated text
            lastASRTranscriptRef.current = newText;
            return newText;
          });
          setProvisionalText('');
          return; // Don't save newline-only segments to DB
        }
        if (hasLeadingNewlines) {
        }

        // Positions are recorded inside the updater, from the text the insert actually
        // lands in — this handler is registered once, so page state here is stale.
        const recordPositions = createOnceGuard();
        const segmentId = isPending ? result.segment_id : undefined;

        // Insert text based on insertion mode (use ref for stable closure access)
        setDictationText((prev) => {
          const textWithSpace = hasLeadingNewlines ? `${leadingNewlines}${asrText} ` : `${asrText} `;
          const leadLength = hasLeadingNewlines ? leadingNewlines.length : 0;
          let result: string;
          const mode = insertionModeRef.current;

          if (mode === 'replace' && !hasReplacedSelection.current) {
            // First result replaces the selection, then switch to insert mode
            const { start, end } = insertionPointRef.current;
            result = prev.slice(0, start) + textWithSpace + prev.slice(end);
            recordPositions(() => {
              shiftPendingSegments(pendingSegmentsRef.current, end, textWithSpace.length - (end - start));
              if (segmentId) pendingSegmentsRef.current.set(segmentId, { text: asrText, position: start + leadLength });
            });
            // Update insertion point to after the inserted text
            insertionPointRef.current = { start: start + textWithSpace.length, end: start + textWithSpace.length };
            hasReplacedSelection.current = true;
            cursorPositionRef.current = start + textWithSpace.length;
          } else if (mode === 'insert' || (mode === 'replace' && hasReplacedSelection.current)) {
            // Insert at cursor position
            const pos = insertionPointRef.current.start;
            result = prev.slice(0, pos) + textWithSpace + prev.slice(pos);
            recordPositions(() => {
              shiftPendingSegments(pendingSegmentsRef.current, pos, textWithSpace.length);
              if (segmentId) pendingSegmentsRef.current.set(segmentId, { text: asrText, position: pos + leadLength });
            });
            // Advance insertion point
            insertionPointRef.current = { start: pos + textWithSpace.length, end: pos + textWithSpace.length };
            cursorPositionRef.current = pos + textWithSpace.length;
          } else {
            // Default: append to end
            const base = hasLeadingNewlines ? prev.trimEnd() : prev;
            result = `${base}${textWithSpace}`;
            recordPositions(() => {
              if (segmentId)
                pendingSegmentsRef.current.set(segmentId, { text: asrText, position: base.length + leadLength });
            });
            cursorPositionRef.current = result.length;
          }

          lastASRTranscriptRef.current = result;
          return result;
        });

        // Update visual cursor position for insert/replace modes
        const mode = insertionModeRef.current;
        if (mode === 'insert' || mode === 'replace') {
          setTargetCursorPosition(insertionPointRef.current.start);
        } else {
          setTargetCursorPosition(null);
        }

        // Clear provisional text when any result arrives
        setProvisionalText('');

        if (segmentId) {
          // Add to display array
          setPendingRefinements((prev) => [...prev, { id: segmentId, text: asrText }]);
        } else {
          // Final result - clear finalization state
          setIsFinalizingTranscription(false);

          // Segments are saved server-side via _schedule_segment_save — no client POST needed
        }
      }
    } catch (error) {
      console.error('[Transcription] Error:', error);
    }
  };

  // Handle transcription update (Phase 2 - large model refinement)
  const handleTranscriptionUpdate = (event: TranscriptionUpdateEvent) => {
    try {
      const pendingSegment = pendingSegmentsRef.current.get(event.segment_id);
      if (!pendingSegment) {
        return;
      }

      // Remove from pending array
      setPendingRefinements((prev) => prev.filter((p) => p.id !== event.segment_id));
      pendingSegmentsRef.current.delete(event.segment_id);

      // If text was refined (changed), update the dictation text in-place
      if (event.was_refined && event.text) {
        const shiftLaterSegments = createOnceGuard();
        setDictationText((prev) => {
          // Find and replace the old text with the new refined text
          const oldText = `${pendingSegment.text} `;
          const newText = `${event.text} `;

          const refined = applyPhase2Refinement(prev, oldText, newText, pendingSegment.position);
          if (refined === null) {
            console.warn('[TranscriptionUpdate] Could not place refinement unambiguously');
            return prev;
          }
          shiftLaterSegments(() =>
            shiftPendingSegments(
              pendingSegmentsRef.current,
              refined.at + oldText.length,
              newText.length - oldText.length
            )
          );
          const result = refined.text;

          // Track this as the last ASR-generated text
          lastASRTranscriptRef.current = result;
          return result;
        });
      }

      // When all pending segments are resolved, finalize cursor position and save
      if (pendingSegmentsRef.current.size === 0) {
        setIsFinalizingTranscription(false);

        // Place cursor right after inserted text by finding where the original suffix starts
        const mode = insertionModeRef.current;
        if ((mode === 'insert' || mode === 'replace') && textAfterCursorRef.current) {
          setDictationText((current) => {
            const suffixPos = current.indexOf(textAfterCursorRef.current, insertionStartRef.current);
            const finalPos = suffixPos !== -1 ? suffixPos : current.length;
            insertionPointRef.current = { start: finalPos, end: finalPos };
            cursorPositionRef.current = finalPos;
            setTargetCursorPosition(finalPos);
            return current;
          });
        }

        // No automatic transcript PUT here — segments are saved server-side in real-time,
        // and smart review saves smart_review_result on pause/stop. Writing to user_transcript
        // here would cause stale Phase 2 text to override the better smart_review_result on
        // resume (user_transcript has higher load priority). Only manual CMD+S saves should
        // write to user_transcript.
      }

      // Segments are saved server-side via _schedule_segment_save — no client POST needed
    } catch (error) {
      console.error('[TranscriptionUpdate] Error:', error);
    }
  };

  // Handle smart review completion (AI cleanup of entire transcript)
  // Server sends full_cleaned_text with accumulated corrected text from all review cycles.
  const handleSmartReviewComplete = (event: SmartReviewCompleteEvent) => {
    setIsSmartReviewing(false);

    // Don't overwrite if user is actively recording or has manually edited during pause
    if (
      !shouldApplyDictationSmartReview({
        isDictating: isDictatingRef.current,
        isPaused: isPausedRef.current,
        currentText: editorRef.current?.innerText || '',
        baselineText: originalTranscriptRef.current || '',
      })
    ) {
      return;
    }

    // Replace entire text with full cleaned version from server
    if (event.full_cleaned_text) {
      const newText = `${event.full_cleaned_text} `;

      setDictationText(() => {
        cursorPositionRef.current = newText.length;
        insertionPointRef.current = { start: newText.length, end: newText.length };
        setTargetCursorPosition(null);
        lastASRTranscriptRef.current = newText;
        return newText;
      });

      originalTranscriptRef.current = newText;

      toast({
        title: 'Umritun endurskoðuð',
        description: `Texti hreinsaður (${event.processing_time_ms}ms)`,
      });
    }
  };

  // Start audio capture and transcription
  const startDictation = async () => {
    setIsInitializing(true);
    try {
      // A session with no id yet is a fresh one (vs resuming an existing session).
      const isNewSession = !sessionIdRef.current;
      // Mint id + navigate + create the session (idempotent) via the shared hook.
      const { patient: resolvedPatient } = await ensureSession();

      // Initialize streaming client
      streamingClientRef.current = new StreamingClient();

      // Set transcription callbacks
      streamingClientRef.current.onTranscription(handleTranscription);
      streamingClientRef.current.onTranscriptionUpdate(handleTranscriptionUpdate); // Phase 2 refinement
      streamingClientRef.current.onConversationSummary((event) => {
        if (event.summary) {
          setSummary(event.summary);
          summaryGeneration.recordFromEvent(event);
          setEditedSummary(null); // Reset edited state when a new summary arrives
        }
      });
      streamingClientRef.current.onTranscriptionStatus((event: TranscriptionStatusEvent) => {
        setTranscriptionStatus(event.status);
        if (event.accumulated_duration !== undefined) {
          setAccumulatedDuration(event.accumulated_duration);
        }
        if (event.target_duration !== undefined) {
          setTargetDuration(event.target_duration);
        }
      });

      // Smart review callbacks
      // Only show spinner when not actively recording (incremental reviews run silently)
      streamingClientRef.current.onSmartReviewStart(() => {
        if (!isDictatingRef.current) {
          setIsSmartReviewing(true);
        }
      });
      streamingClientRef.current.onSmartReviewComplete(handleSmartReviewComplete);
      streamingClientRef.current.onSmartReviewError((event) => {
        console.error('[Dictation] Smart review error:', event.error);
        setIsSmartReviewing(false);
        if (!isDictatingRef.current) {
          toast({
            title: 'Villa',
            description: 'Ekki tókst að endurskoða umritun',
            variant: 'destructive',
          });
        }
      });
      streamingClientRef.current.onTasksDetected((event) => {
        handleServerTasks(event);
      });

      // Streaming dictation (beta): a failed or reconnecting stream leaves its
      // partial text stale — clear it. Real problems get a non-blocking toast.
      streamingClientRef.current.onStreamingWarning((event: StreamingWarningEvent) => {
        setProvisionalText('');
        const description = getStreamingWarningMessage(event);
        if (description) {
          toast({ title: 'Umritun', description });
        }
      });
      streamingClientRef.current.onStreamingProvisionalClear(() => {
        setProvisionalText('');
      });

      // Connection health monitoring
      streamingClientRef.current.onConnectionStateChange(handleConnectionStateChange);

      // Connect to WebSocket
      await streamingClientRef.current.connect();
      await streamingClientRef.current.startSession(
        sessionIdRef.current,
        selectedLanguage,
        resolvedPatient?.dbId?.toString(),
        'dictation',
        {
          fhirPatientId: resolvedPatient?.fhirPatientId,
          enableRuleBasedNormalization,
          clinicalTemplateId: selectedTemplateId ?? undefined,
          summaryLanguage: selectedSummaryLanguage,
          asrModelPreference,
          streamingDictation,
          startReason: isNewSession ? 'new' : 'resume',
        }
      );

      // Start capturing, streaming PCM to the server
      await startCapture(selectedDeviceId);
      microphone.markGranted();

      // Only clear dictation text for new sessions, not when resuming
      if (!isResumedSession) {
        setDictationText('');
        summaryGeneration.reset();
      }
      setProvisionalText('');

      // Track text length at start of this recording to detect new finalized text
      textLengthAtRecordingStartRef.current = dictationText.trim().length;

      // Determine insertion mode from cursor position (if text already exists)
      const { start, end } = selectionRangeRef.current;
      hasReplacedSelection.current = false;

      if (dictationText.length > 0 && start !== end) {
        // User selected text before starting — replace mode
        setInsertionMode('replace');
        insertionModeRef.current = 'replace';
        insertionPointRef.current = { start, end };
        insertionStartRef.current = start;
        textAfterCursorRef.current = dictationText.slice(end);
      } else if (dictationText.length > 0 && start < dictationText.length) {
        // Cursor is mid-text — insert at cursor
        setInsertionMode('insert');
        insertionModeRef.current = 'insert';
        insertionPointRef.current = { start, end: start };
        insertionStartRef.current = start;
        textAfterCursorRef.current = dictationText.slice(start);
      } else {
        // No text or cursor at end — append
        setInsertionMode('append');
        insertionModeRef.current = 'append';
        insertionPointRef.current = { start: dictationText.length, end: dictationText.length };
        insertionStartRef.current = dictationText.length;
        textAfterCursorRef.current = '';
      }

      setIsDictating(true);
      isDictatingRef.current = true;
      setIsInitializing(false);

      // Clear previous recording
      if (recordedAudioUrl) {
        URL.revokeObjectURL(recordedAudioUrl);
        setRecordedAudioUrl(null);
      }

      toast({
        title: 'Upptaka hafin',
        description: 'Talaðu við hljóðnemann...',
      });
    } catch (error) {
      console.error('[Dictation] Start error:', error);
      setIsInitializing(false);
      setIsRecording(false);
      if (microphone.reportError(error)) {
        streamingClientRef.current?.disconnect();
        streamingClientRef.current = null;
        return;
      }
      toast({
        title: 'Villa',
        description: 'Ekki tókst að hefja upptöku',
        variant: 'destructive',
      });
    }
  };

  // Stop audio capture and transcription (kept for potential future use)
  const _stopDictation = async () => {
    // IMPORTANT: Stop PCM audio manager FIRST to stop capturing/sending audio immediately
    // This must happen before any async operations (like WAV upload) to prevent
    // audio from continuing to stream while we process other things
    if (pcmAudioManagerRef.current) {
      await pcmAudioManagerRef.current.stop();
    }

    // Stop streaming session (tell server to stop expecting audio)
    if (streamingClientRef.current) {
      await streamingClientRef.current.stopSession(selectedSummaryLanguage);
      // Don't disconnect yet - wait for final transcription
      // The connection will be closed when final arrives or after timeout
    }

    // Show finalization state if we recorded but didn't get NEW finalized transcription
    // Compare current text length to length at start of THIS recording session
    // This handles resumed sessions where text already exists from previous recordings
    const gotNewFinalizedText = dictationText.trim().length > textLengthAtRecordingStartRef.current;
    if (elapsedTime > 0 && !gotNewFinalizedText) {
      setIsFinalizingTranscription(true);
    }

    // Create WAV file from recorded PCM data and upload to storage
    if (pcmAudioManagerRef.current) {
      try {
        const snap = pcmAudioManagerRef.current.getUploadSnapshot();
        const allPCM = snap.pcm;
        const sampleRate = snap.sampleRate;

        if (allPCM.length > 0) {
          // Calculate expected duration - if sampleRate is wrong, this will be very different from elapsedTime
          const _calculatedDuration = allPCM.length / sampleRate;

          const wavBlob = pcmToWav(allPCM, sampleRate);
          const wavUrl = URL.createObjectURL(wavBlob);

          // Clean up previous URL if exists
          if (recordedAudioUrl) {
            URL.revokeObjectURL(recordedAudioUrl);
          }

          setRecordedAudioUrl(wavUrl);

          // Upload audio through backend (proxied upload)
          // Only upload if clinic setting allows it
          if (sessionIdRef.current && storeAudioInBlob) {
            try {
              const uploadResp = await uploadCaptureAudio(sessionIdRef.current, snap, wavBlob);

              if (uploadResp.ok) {
                const uploadData = await uploadResp.json();
                // Update URL to server URL for persistence across refreshes
                if (uploadData.audio_url) {
                  URL.revokeObjectURL(wavUrl);
                  setRecordedAudioUrl(uploadData.audio_url);
                }
              } else {
                const errorText = await uploadResp.text();
                console.error('[Dictation] Upload failed:', uploadResp.status, errorText);
              }
            } catch (uploadError) {
              console.error('[Dictation] Error uploading audio:', uploadError);
            }
          } else if (sessionIdRef.current && !storeAudioInBlob) {
          }
        }
      } catch (error) {
        console.error('[Dictation] Error creating WAV:', error);
      }
    }

    // Clear PCM audio manager ref (already stopped at the beginning of stopDictation)
    pcmAudioManagerRef.current = null;

    setIsDictating(false);
    isDictatingRef.current = false;
    setProvisionalText('');
    setTranscriptionStatus('idle');
    setAccumulatedDuration(0);

    toast({
      title: 'Upptöku lokið',
      description: 'Bíð eftir lokatalgreiningu...',
    });
  };

  // Pause audio capture (keeps session open for resume)
  const pauseDictation = async () => {
    if (!pcmAudioManagerRef.current) return;

    // Reset abort flag for this pause cycle
    pauseAbortRef.current = false;

    // Stop timer immediately by setting paused state FIRST
    setIsPaused(true);
    isPausedRef.current = true;

    // Show processing state
    setIsFinalizingTranscription(true);

    // Mark as not dictating before flush so smart review results are accepted
    // (handleSmartReviewComplete skips updates while isDictatingRef is true)
    isDictatingRef.current = false;

    // Pause capture and flush the server buffer so pending audio gets Phase 2
    await pauseCapture();

    // Wait a moment for any pending transcription to arrive from server
    await new Promise((resolve) => setTimeout(resolve, 2000));

    // If user already resumed, abort the rest of the pause cleanup
    if (pauseAbortRef.current) return;

    // If user already resumed, abort the rest of the pause cleanup
    if (pauseAbortRef.current) return;

    // Upload audio to blob storage (appends to existing)
    try {
      const snap = pcmAudioManagerRef.current.getUploadSnapshot();
      const allPCM = snap.pcm;
      const sampleRate = snap.sampleRate;

      if (allPCM.length > 0 && storeAudioInBlob && sessionIdRef.current) {
        const wavBlob = pcmToWav(allPCM, sampleRate);

        const uploadResp = await uploadCaptureAudio(sessionIdRef.current, snap, wavBlob);

        if (uploadResp.ok) {
          const uploadData = await uploadResp.json();
          if (uploadData.audio_url) {
            // Clean up old local URL if exists
            if (recordedAudioUrl?.startsWith('blob:')) {
              URL.revokeObjectURL(recordedAudioUrl);
            }
            setRecordedAudioUrl(uploadData.audio_url);
          }
          // Clear PCM data after successful upload
          pcmAudioManagerRef.current?.clearPCMData();
        } else {
          console.error('[Dictation] Pause upload failed:', uploadResp.status);
        }
      }
    } catch (error) {
      console.error('[Dictation] Error uploading audio on pause:', error);
    } finally {
      // Only clear finalization state if not aborted (user didn't resume)
      if (!pauseAbortRef.current) {
        setIsFinalizingTranscription(false);
      }
    }

    // If user already resumed, don't overwrite their state
    if (pauseAbortRef.current) return;

    setIsDictating(false);
    isDictatingRef.current = false;

    // Transcript is saved after all Phase 2 refinements complete (in handleTranscriptionUpdate)

    // Clear provisional text - don't append it because:
    // 1. Phase 1/2 transcription already provides the "real" text
    // 2. Instant feedback can arrive AFTER Phase 1, causing duplicates
    setProvisionalText('');
    // isPaused already set at the start for immediate timer stop

    toast({
      title: 'Upptaka í bið',
      description: 'Smelltu á takka til að halda áfram...',
    });
  };

  // Resume audio capture after pause
  const resumeDictation = async () => {
    // Abort any in-flight pause cleanup
    pauseAbortRef.current = true;

    if (!pcmAudioManagerRef.current || !streamingClientRef.current) {
      // If refs are gone, we need to start fresh
      setInsertionMode('append');
      insertionModeRef.current = 'append';
      setIsRecording(true);
      setIsPaused(false);
      isPausedRef.current = false;
      startDictation();
      return;
    }

    // Determine insertion mode based on cursor position / selection
    const { start, end } = selectionRangeRef.current;
    const textLength = dictationText.length;
    hasReplacedSelection.current = false;

    if (start !== end) {
      // User has selected text — replace selection mode
      setInsertionMode('replace');
      insertionModeRef.current = 'replace';
      insertionPointRef.current = { start, end };
    } else if (start < textLength) {
      // Cursor is mid-text — insert at cursor
      setInsertionMode('insert');
      insertionModeRef.current = 'insert';
      insertionPointRef.current = { start, end: start };
    } else {
      // Cursor at end — default append
      setInsertionMode('append');
      insertionModeRef.current = 'append';
      insertionPointRef.current = { start: textLength, end: textLength };
    }

    // Track text length at start of this recording segment
    textLengthAtRecordingStartRef.current = dictationText.trim().length;

    // Resume PCM capture (also clears the silence stopped-banner)
    try {
      await resumeCapture(selectedDeviceId);
      microphone.markGranted();
    } catch (e) {
      if (microphone.reportError(e)) return;
      console.error('[Dictation] Resume error:', e);
      toast({ title: 'Villa', description: 'Ekki tókst að halda upptöku áfram', variant: 'destructive' });
      return;
    }

    setIsDictating(true);
    isDictatingRef.current = true;
    setIsPaused(false);
    isPausedRef.current = false;

    const modeDescriptions = {
      append: 'Talaðu við hljóðnemann...',
      insert: 'Setji inn við bendil...',
      replace: 'Skipti út völdu texta...',
    };

    toast({
      title: 'Upptaka heldur áfram',
      description: modeDescriptions[insertionMode === 'append' ? 'append' : start !== end ? 'replace' : 'insert'],
    });
  };

  const toggleRecording = () => {
    if (!isRecording) {
      // Not recording at all - start new session
      setIsRecording(true);
      setIsPaused(false);
      isPausedRef.current = false;
      startDictation();
    } else if (isPaused) {
      // Recording session exists but paused - resume
      resumeDictation();
    } else {
      // Actively recording - pause
      pauseDictation();
    }
  };
  // Note: Patient state (selectedPatient, recentPatients, etc.) is now handled by usePatientSearch hook

  // Autosave summary draft to localStorage (debounced, 5 seconds after last edit)
  // biome-ignore lint/correctness/useExhaustiveDependencies: sessionIdRef is a stable ref (from useSession); reading .current must not be a dependency
  useEffect(() => {
    if (!editedSummary || !sessionIdRef.current) return;

    const timeoutId = setTimeout(() => {
      const draftKey = `summaryDraft_${sessionIdRef.current}`;
      localStorage.setItem(
        draftKey,
        JSON.stringify({
          text: editedSummary,
          timestamp: Date.now(),
          templateId: selectedTemplateId,
          generationId: summaryGeneration.generationId(),
        })
      );
    }, 5000); // 5 second debounce

    return () => clearTimeout(timeoutId);
  }, [editedSummary, selectedTemplateId, summaryGeneration]);

  // Clear draft from localStorage (called after explicit save)
  const clearSummaryDraft = () => {
    if (sessionIdRef.current) {
      localStorage.removeItem(`summaryDraft_${sessionIdRef.current}`);
    }
  };

  // Handle template change from SmartPanel. `persist: false` means the panel
  // is clearing a selection it couldn't resolve in this org — update the local
  // selection but never write that to the server-side default.
  const handleTemplateChange = (templateId: number | null, options?: { persist?: boolean }) => {
    hasPickedTemplateRef.current = true;
    setSelectedTemplateId(templateId);
    if (options?.persist !== false) {
      setTemplateDefault('dictation', templateId);
    }
  };

  // Handle summary edit
  const handleSummaryEdit = (text: string) => {
    setEditedSummary(text);
  };

  // Persist a full note text to `targetSessionId` and make it the saved summary (throws on
  // failure). Shared by manual save and the note-instruction composer (apply/undo); a composer
  // reply that arrives after the page switched session must not land in the new one.
  const persistNoteText = async (text: string, targetSessionId: string) => {
    if (!targetSessionId || targetSessionId !== sessionIdRef.current) {
      throw new Error('Skipt var um lotu — breytingin var ekki vistuð');
    }
    if (!selectedTemplateId) {
      throw new Error('Velja þarf sniðmát til að vista samantekt');
    }
    await saveSummaryMutation.mutateAsync({
      sessionId: targetSessionId,
      summaryText: text,
      templateId: selectedTemplateId,
      source: 'save',
      viewingAsAuthAccountId,
      generationId: summaryGeneration.generationId(),
    });
    // Saved to the target session; if the page moved on meanwhile, leave the new session's editor alone.
    if (targetSessionId !== sessionIdRef.current) return;
    setSummary(text); // Update original
    setEditedSummary(null); // Clear edit state
    clearSummaryDraft(); // Clear localStorage draft
  };

  // Beta note-instruction composer: owned here so desktop + mobile panels share one history.
  const noteInstructions = useNoteInstructions({
    sessionId: sessionIdRef.current || undefined,
    patientId: selectedPatient?.dbId,
    persistNote: persistNoteText,
    currentNote: editedSummary ?? summary,
    currentTasks: tasks,
  });

  // Save user-edited summary
  const handleSummarySave = async () => {
    if (!editedSummary || !sessionIdRef.current || !selectedTemplateId) {
      toast({
        title: 'Velja þarf sniðmát',
        description: 'Veldu sniðmát til að vista samantekt.',
        variant: 'destructive',
      });
      return;
    }

    try {
      await persistNoteText(editedSummary, sessionIdRef.current);
      toast({ title: 'Samantekt vistuð' });
    } catch (error) {
      console.error('[Dictation] Error saving summary:', error);
      toast({
        title: 'Villa',
        description: error instanceof Error ? error.message : 'Ekki tókst að vista samantekt',
        variant: 'destructive',
      });
    }
  };

  // Persist + audit the copied summary (fire-and-forget; never blocks the clipboard copy)
  const handleSummaryCopy = (text: string) => {
    if (!text || !sessionIdRef.current || !selectedTemplateId) return;
    copySummaryMutation.mutate(
      {
        sessionId: sessionIdRef.current,
        summaryText: text,
        templateId: selectedTemplateId,
        source: 'copy',
        viewingAsAuthAccountId,
        generationId: summaryGeneration.generationId(),
      },
      { onError: (err) => console.error('[Dictation] copy-persist failed', err) }
    );
  };

  // Handle user edits to transcript (track dirty state)
  const handleTranscriptChange = (newText: string) => {
    // Check if this is a user edit (not ASR update)
    // ASR updates always append/replace in specific patterns
    // User edits happen when recording is stopped and text changes differently
    const wasUserEdit = !isDictating && !isFinalizingTranscription && newText !== lastASRTranscriptRef.current;
    if (newText !== lastASRTranscriptRef.current) invalidatePendingPositions(pendingSegmentsRef.current);

    setDictationText(newText);

    if (wasUserEdit && !isTranscriptModified) {
      setIsTranscriptModified(true);
    }
  };

  // Save user-edited transcript to database
  const saveTranscript = async () => {
    if (!sessionIdRef.current || !isTranscriptModified || isSavingTranscript) return;

    // Read current text directly from DOM to ensure we have the latest
    // (React state may be stale if user pressed CMD+S while typing)
    const currentText = editorRef.current?.innerText || dictationText;

    setIsSavingTranscript(true);
    try {
      const response = await fetch(`/api/sessions/${sessionIdRef.current}/transcript`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ text: currentText }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to save transcript');
      }

      setIsTranscriptModified(false);
      originalTranscriptRef.current = currentText;
      lastASRTranscriptRef.current = currentText;
      // Sync React state with current DOM text
      setDictationText(currentText);
      toast({ title: 'Umritun vistuð' });

      // Regenerate summary with the updated transcript (pass text directly since state update is async)
      await generateSummary(currentText);
    } catch (error) {
      console.error('[Dictation] Error saving transcript:', error);
      toast({
        title: 'Villa',
        description: error instanceof Error ? error.message : 'Ekki tókst að vista umritun',
        variant: 'destructive',
      });
    } finally {
      setIsSavingTranscript(false);
    }
  };

  // Generate summary using templates
  // Optional transcriptOverride param allows passing the current text directly (for save-then-regenerate flow)
  // First-run tour: the sample text it put on the page.
  const tutorialDemoTextRef = useRef('');
  const tutorialDemoNote = useTutorialDemoNote();

  const generateSummary = async (transcriptOverride?: string, style?: SummaryStyle) => {
    if (isRecording && !isPaused) return;
    // SmartPanel disables its buttons without a template; this also covers the
    // automatic regenerate paths (transcript save, language change).
    if (selectedTemplateId == null) return;

    const transcriptToUse = transcriptOverride || dictationText;
    if (!transcriptToUse.trim()) {
      toast({
        title: 'Enginn texti',
        description: 'Engin diktering til að gera samantekt úr.',
        variant: 'destructive',
      });
      return;
    }

    const noteRun = tutorialDemoNote.startNote();
    try {
      const data = await requestSummary({
        transcript: transcriptToUse,
        mode: 'dictation',
        template_id: selectedTemplateId ?? undefined,
        session_id: sessionIdRef.current || undefined,
        language: selectedSummaryLanguage,
        pre_encounter_context: preEncounterContextRef.current || undefined,
        style,
        regen_trigger: summaryGeneration.regenTrigger(selectedTemplateId, Boolean(summary)),
      });
      if (noteRun.isStale()) return;
      setSummary(data.soap_note);
      noteRun.markShown();
      summaryGeneration.recordFromHttp(data, selectedTemplateId);
      setEditedSummary(null);
      // Reconcile with the persisted task list (stop/pause detection writes it).
      refreshTasks();
      toast({
        title: 'Samantekt tilbúin',
        description: data.template_used ? `Sniðmát: ${data.template_used}` : undefined,
      });
    } catch (error) {
      console.error('[Dictation] Error generating summary:', error);
      toast({
        title: 'Villa',
        description: error instanceof Error ? error.message : 'Ekki tókst að búa til samantekt',
        variant: 'destructive',
      });
    }
  };

  const handleGenerateSummaryClick = (style?: SummaryStyle) => {
    void generateSummary(undefined, style);
  };

  // Save panel width to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('smartPanelWidth', rightPanelWidth.toString());
  }, [rightPanelWidth]);

  // Save selected microphone to localStorage whenever it changes
  useEffect(() => {
    if (selectedDeviceId) {
      localStorage.setItem('selectedMicrophoneId', selectedDeviceId);
    }
  }, [selectedDeviceId]);

  // Keyboard shortcut for saving transcript (CMD+S / CTRL+S)
  // biome-ignore lint/correctness/useExhaustiveDependencies: saveTranscript is stable, we only want to re-attach when modified/saving state changes
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 's') {
        e.preventDefault();
        if (isTranscriptModified && !isSavingTranscript) {
          saveTranscript();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isTranscriptModified, isSavingTranscript]);

  // Auto-regenerate summary when summary language changes (if there's existing text and summary)
  const prevSummaryLanguageRef = useRef(selectedSummaryLanguage);
  // biome-ignore lint/correctness/useExhaustiveDependencies: Only trigger on language change, not when other values change
  useEffect(() => {
    // Only regenerate if language actually changed (not on initial mount)
    if (prevSummaryLanguageRef.current !== selectedSummaryLanguage) {
      prevSummaryLanguageRef.current = selectedSummaryLanguage;
      // Only regenerate if there's text and a summary already exists
      if (dictationText.trim() && summary && !isGeneratingSummary && !isDictating) {
        generateSummary();
      }
    }
  }, [selectedSummaryLanguage]);

  // Handle finalization completion - disconnect WebSocket and add timeout fallback
  useEffect(() => {
    let timeoutId: NodeJS.Timeout | null = null;

    if (isFinalizingTranscription) {
      // Set timeout to clear finalization state if no transcription arrives within 90 seconds
      timeoutId = setTimeout(() => {
        setIsFinalizingTranscription(false);
        // Disconnect WebSocket if still connected
        if (streamingClientRef.current) {
          streamingClientRef.current.disconnect();
          streamingClientRef.current = null;
        }
        toast({
          title: 'Umritun tókst ekki',
          description: 'Engin umritun barst. Reyndu aftur.',
          variant: 'destructive',
        });
      }, 90000); // 90 second timeout
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [isFinalizingTranscription, toast, streamingClientRef]);

  // Note: Language fetching is now handled by useLanguages hook

  // Load existing session data when resuming
  // biome-ignore lint/correctness/useExhaustiveDependencies: setElapsedTime is a stable state setter, only run when resumeSessionId changes
  useEffect(() => {
    async function loadExistingSession() {
      if (!resumeSessionId) return;

      setIsLoadingSession(true);

      try {
        // Set session ID first
        sessionIdRef.current = resumeSessionId;
        setIsResumedSession(true);

        // Try loading from live-conversation endpoint first (streaming segments)
        const liveResponse = await fetch(`/api/sessions/${resumeSessionId}/live-conversation`, {
          credentials: 'include',
        });

        let segments: TranscriptionSegment[] = [];

        let liveData: LiveSessionData | null = null;
        if (!liveResponse.ok) patientSearch.reconcileWithSessionPatient(null);
        if (liveResponse.ok) {
          const responseData: LiveSessionData = await liveResponse.json();
          liveData = responseData;
          patientSearch.reconcileWithSessionPatient(responseData.audio?.patient_id ?? null);
          if (responseData.segments && responseData.segments.length > 0) {
            segments = responseData.segments;
          }
          // Load audio URL if available (from blob storage)
          if (responseData.audio?.audio_url) {
            setRecordedAudioUrl(responseData.audio.audio_url);
          }

          // Restore elapsed time from audio duration or last segment timestamp
          // Note: If API doesn't return duration, the onLoadedMetadata handler will set it from audio element
          if (responseData.audio?.duration) {
            setElapsedTime(Math.round(responseData.audio.duration));
          } else if (segments.length > 0) {
            const lastSegment = segments[segments.length - 1];
            const lastTime = lastSegment.end_time || lastSegment.start_time || 0;
            if (lastTime > 0) {
              setElapsedTime(Math.round(lastTime));
            }
            // If lastTime is 0, the onLoadedMetadata handler will set elapsed time from audio element
          }

          // Load existing summary - prefer user_summary if available, otherwise model_summary
          // Note: model_summary can be in separate table (model_summary.summary_text) or in audio record (audio.model_summary)
          // Echo the generation behind whichever text ends up displayed below, so
          // saves after reopening pair with that generation (or reset, if the
          // displayed text came from an audio-record fallback with no known id).
          summaryGeneration.recordFromHttp(summaryEchoForLoadedSession(responseData), null);
          let loadedSummary = '';
          if (responseData.user_summary?.summary_text) {
            loadedSummary = responseData.user_summary.summary_text;
            setSummary(loadedSummary);
            if (responseData.user_summary.selected_template_id) {
              restoreTemplateSelection(responseData.user_summary.selected_template_id);
            }
          } else if (responseData.model_summary?.summary_text) {
            loadedSummary = responseData.model_summary.summary_text;
            setSummary(loadedSummary);
            if (responseData.model_summary.selected_template_id) {
              restoreTemplateSelection(responseData.model_summary.selected_template_id);
            }
          } else if (responseData.audio?.user_summary) {
            // Fallback: user_summary stored directly in audio record (preferred over model_summary)
            loadedSummary = responseData.audio.user_summary;
            setSummary(loadedSummary);
            if (responseData.audio.selected_template_id) {
              restoreTemplateSelection(responseData.audio.selected_template_id);
            }
          } else if (responseData.audio?.model_summary) {
            // Fallback: model_summary stored directly in audio record
            loadedSummary = responseData.audio.model_summary;
            setSummary(loadedSummary);
            if (responseData.audio.selected_template_id) {
              restoreTemplateSelection(responseData.audio.selected_template_id);
            }
          }

          // Check for localStorage draft (autosaved work in progress)
          const draftKey = `summaryDraft_${resumeSessionId}`;
          const draftJson = localStorage.getItem(draftKey);
          if (draftJson) {
            try {
              const draft = JSON.parse(draftJson);
              // Only restore if draft is different from loaded summary
              if (draft.text && draft.text !== loadedSummary) {
                setEditedSummary(draft.text);
                summaryGeneration.recordFromHttp(summaryEchoForDraft(draft), null);
                if (draft.templateId) {
                  restoreTemplateSelection(draft.templateId);
                }
                toast({
                  title: 'Drög endurheimt',
                  description: 'Óvistaðar breytingar fundust og voru endurheimt.',
                });
              }
            } catch (e) {
              console.error('[Dictation] Error parsing localStorage draft:', e);
            }
          }
        }

        // If no live segments, try regular segments endpoint
        if (segments.length === 0) {
          const segmentsResponse = await fetch(`/api/sessions/${resumeSessionId}/segments`, {
            credentials: 'include',
          });
          if (segmentsResponse.ok) {
            const segmentsData = await segmentsResponse.json();
            if (Array.isArray(segmentsData) && segmentsData.length > 0) {
              segments = segmentsData;
            }
          }
        } else {
        }

        // Priority order for loading transcript:
        // 1. user_transcript (user-edited version)
        // 2. segments (single source of truth; carries smart-review corrected text)
        let loadedTranscript = '';

        // Extract values with null-safe access
        const userTranscript = liveData?.audio?.user_transcript;

        // Check for user_transcript first (user-edited version takes priority)
        if (userTranscript) {
          loadedTranscript = userTranscript;
          setDictationText(loadedTranscript);
          cursorPositionRef.current = loadedTranscript.length;
          originalTranscriptRef.current = loadedTranscript;
          lastASRTranscriptRef.current = loadedTranscript;
        }
        // Otherwise build from segments (the retired smart_review_result blob is
        // gone; corrected text now lives on the segment rows themselves).
        else if (segments.length > 0) {
          // Populate saved-segments set (dedup on continue) from segment texts.
          for (const s of segments) {
            if (s.text?.trim()) {
              savedSegmentsRef.current.add(s.text.trim());
            }
          }

          // Chronological, de-duplicated joined transcript (shared helper).
          const existingText = textFromSegments(segments);
          if (existingText) {
            loadedTranscript = `${existingText} `;
            setDictationText(loadedTranscript);
            cursorPositionRef.current = loadedTranscript.length;
            originalTranscriptRef.current = loadedTranscript;
            lastASRTranscriptRef.current = loadedTranscript;
          }
        }
      } catch (error) {
        console.error('[Dictation] Error loading session:', error);
      } finally {
        setIsLoadingSession(false);
      }
    }

    loadExistingSession();
  }, [resumeSessionId]);

  // Note: Audio devices are now handled by useAudioDevices hook
  // Note: Timer is now handled by useRecordingTimer hook
  // Note: formatSSN and extractDOB are now imported from sessionUtils

  // Note: searchPatients and addToRecentPatients are now defined near the top of the component
  // using utilities from sessionUtils

  // Mirror auth-org audio storage setting from /api/auth/user.
  useEffect(() => {
    setStoreAudioInBlob(user?.orgSettings?.storeAudioInBlob ?? false);
  }, [user?.orgSettings?.storeAudioInBlob]);

  const hasVisibleTranscript = dictationText.trim().length > 0 || provisionalText.trim().length > 0;
  const showMobileRecordingButton = shouldShowCompactRecordingControl({
    isRecording,
    isInitializing,
    isPaused,
    isFinalizingTranscription,
    hasContent: hasVisibleTranscript,
  });
  const recordingDisabledReason = isViewingAsDelegate ? 'Ekki hægt að taka upp á vegum annars notanda' : undefined;
  // The first-run tour plays a fictional dictation in here, then removes it with any note made from it.
  const tutorialDemo: SessionTutorialDemo = {
    showLine: (line) => {
      tutorialDemoNote.markSampleShown();
      tutorialDemoTextRef.current = appendDemoText(tutorialDemoTextRef.current, line.text);
      setDictationText((current) => appendDemoText(current, line.text));
    },
    // Only what the tour added: text the person wrote, and a note not made from the sample, stay.
    clear: () => {
      const demoText = tutorialDemoTextRef.current;
      tutorialDemoTextRef.current = '';
      setDictationText((current) => withoutDemoText(current, demoText));
      if (!tutorialDemoNote.clearSample()) return;
      setSummary('');
      setEditedSummary(null);
    },
  };

  return (
    <SessionViewport
      tutorial={{
        mode: 'dictation',
        demo: tutorialDemo,
        enabled: !resumeSessionId && !isViewingAsDelegate,
        paused: (isRecording && !isPaused) || isInitializing,
      }}
    >
      <Sidebar />

      <div className="flex min-w-0 flex-1 flex-col">
        <Header title="" centerContent={!isResumedSession ? <ModeSwitcher activeMode="dictation" /> : undefined}>
          <div className="flex items-center gap-2 md:gap-4">
            {/* Timer - desktop only, mobile timer is in patient info bar */}
            <div
              className={`hidden items-center gap-2 rounded-full border px-2.5 py-1.5 md:flex md:px-3 md:py-1.5 ${
                pipelineStalled && isRecording
                  ? 'border-amber-300 bg-amber-50 text-amber-700'
                  : connectionState === 'reconnecting' && isRecording
                    ? 'border-orange-300 bg-orange-50 text-orange-700'
                    : connectionState === 'disconnected' && isRecording
                      ? 'border-red-400 bg-red-100 text-red-700'
                      : isRecording && !isPaused
                        ? 'border-red-200 bg-red-50 text-red-600'
                        : isPaused
                          ? 'border-amber-200 bg-amber-50 text-amber-600'
                          : 'border-slate-200 bg-slate-50 text-slate-600'
              }`}
            >
              <div
                className={`h-2 w-2 rounded-full ${
                  pipelineStalled && isRecording
                    ? 'bg-amber-500'
                    : connectionState === 'reconnecting' && isRecording
                      ? 'animate-ping bg-orange-500'
                      : connectionState === 'disconnected' && isRecording
                        ? 'bg-red-600'
                        : isRecording && !isPaused
                          ? 'animate-pulse bg-red-500'
                          : isPaused
                            ? 'bg-amber-500'
                            : 'bg-slate-400'
                }`}
              />
              <span className="font-medium font-mono text-xs md:text-sm">
                {isLoadingSession || (isResumedSession && elapsedTime === 0 && recordedAudioUrl) ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  formatTime(elapsedTime)
                )}
              </span>
            </div>

            {/* Mobile Settings Button - opens mic/language selection */}
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="ghost" size="default" className="h-10 w-10 p-0 md:hidden [&_svg]:size-5.5">
                  <Settings />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-72 p-3" align="end">
                <div className="space-y-3">
                  <div className="space-y-1.5">
                    <span className="font-medium text-muted-foreground text-xs">Hljóðnemi</span>
                    <Select value={selectedDeviceId} onValueChange={setSelectedDeviceId}>
                      <SelectTrigger className="h-9 w-full text-sm">
                        <SelectValue placeholder="Veldu hljóðnema" />
                      </SelectTrigger>
                      <SelectContent>
                        {devices.map((device) => (
                          <SelectItem key={device.deviceId} value={device.deviceId} className="text-sm">
                            {device.label || `Hljóðnemi ${device.deviceId.slice(0, 5)}`}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <span className="font-medium text-muted-foreground text-xs">Tungumál</span>
                    <LanguageSelector
                      languages={languages}
                      transcriptionLanguage={selectedLanguage}
                      summaryLanguage={selectedSummaryLanguage}
                      onTranscriptionChange={setSelectedLanguage}
                      onSummaryChange={setSelectedSummaryLanguage}
                      disabled={isDictating}
                    />
                  </div>
                </div>
              </PopoverContent>
            </Popover>

            {/* New Session Button */}
            <Button
              variant="outline"
              size="sm"
              className="h-10 gap-1.5 px-2.5 md:h-8 md:gap-1.5 md:px-3"
              onClick={() => {
                clearSelectedPatient();
                navigate('/session/new');
              }}
            >
              <Plus className="h-4 w-4" />
              <span className="sr-only sm:not-sr-only">Ný lota</span>
            </Button>
          </div>
        </Header>

        <main className="flex min-h-0 flex-1 overflow-hidden">
          {/* Left Panel: Transcript / Recording - hidden on mobile when viewing assistant */}
          <div
            className={`flex min-h-0 min-w-0 flex-1 flex-col bg-white ${mobileView === 'assistant' ? 'hidden md:flex' : ''}`}
          >
            {/* Patient Info Bar */}
            <div className={layoutClasses.columnHeader}>
              <div className="min-w-0 flex-[1_1_200px] md:max-w-sm">
                <PatientSearchSelect {...patientSearch} />
              </div>

              <RecordingControls
                audioQuality={audioQuality}
                isRecording={isRecording}
                isPaused={isPaused}
                isInitializing={isInitializing}
                isFinalizingTranscription={isFinalizingTranscription}
                elapsedTime={elapsedTime}
                onToggleRecording={toggleRecording}
                selectedDeviceId={selectedDeviceId}
                devices={devices}
                onDeviceChange={setSelectedDeviceId}
                isViewingAsDelegate={isViewingAsDelegate}
                showMobileRecordingButton={showMobileRecordingButton}
              />
            </div>

            <MicrophoneNotice
              access={microphone}
              onRetry={retryMicrophone}
              disabled={isInitializing || (isRecording && !isPaused) || isViewingAsDelegate}
            />

            <PipelineStallBanner
              isStalled={pipelineStalled}
              isRecording={isRecording}
              onRecover={handleManualRecovery}
            />
            <RecordingSilenceBanner
              phase={silencePhase}
              secondsRemaining={silenceSecondsRemaining}
              onCancel={cancelSilenceCountdown}
              onResume={toggleRecording}
            />

            {/* Transcript Area - hidden on mobile when viewing assistant */}
            <div
              className={`relative min-h-0 flex-1 overflow-hidden ${mobileView === 'assistant' ? 'hidden md:block' : ''}`}
            >
              {isLoadingSession ? (
                <div className="absolute inset-0 flex flex-col items-center justify-center p-8 text-center text-slate-400">
                  <div className="mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-slate-50">
                    <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                  </div>
                  <h3 className="mb-2 font-medium text-lg text-slate-600">Hleð gögnum...</h3>
                  <p className="max-w-sm">Sæki fyrri upptöku</p>
                </div>
              ) : !dictationText && !isDictating && isFinalizingTranscription ? (
                <div className="absolute inset-0 flex flex-col items-center justify-center p-8 text-center text-slate-500">
                  <div className="mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-slate-50">
                    <div className="h-8 w-8 animate-spin rounded-full border-2 border-slate-200 border-t-primary" />
                  </div>
                  <h3 className="mb-2 font-medium text-lg text-slate-600">Vinn úr upptöku...</h3>
                  <p className="max-w-sm text-slate-400 text-sm">Klára umritun. Þetta getur tekið smá stund.</p>
                </div>
              ) : !dictationText && !isDictating && isInitializing ? (
                <div className="absolute inset-0 flex flex-col items-center justify-center p-8 text-center text-slate-500">
                  <div className="mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-slate-50">
                    <div className="h-8 w-8 animate-spin rounded-full border-2 border-slate-200 border-t-primary" />
                  </div>
                  <h3 className="mb-2 font-medium text-lg text-slate-600">Tengist...</h3>
                  <p className="max-w-sm text-slate-400 text-sm">
                    Undirbý upptöku. Bíddu þangað til þetta hverfur áður en þú byrjar að tala.
                  </p>
                </div>
              ) : !hasVisibleTranscript && !isDictating && !isPaused ? (
                <RecordingEmptyState
                  className="absolute inset-0"
                  onStart={toggleRecording}
                  disabled={isViewingAsDelegate}
                  disabledReason={recordingDisabledReason}
                  desktopPrompt="Notaðu upptökuhnappinn hér að ofan til að hefja dikteringu."
                />
              ) : (
                <ScrollArea className="h-full bg-slate-50/30 px-6 py-6">
                  <div className="w-full">
                    <TutorialAnchor step="demo">
                      <div className="min-h-[400px] rounded-2xl border border-slate-200 bg-white p-8 shadow-sm">
                        <div className="mb-6 flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div
                              className={`flex items-center gap-2 font-medium ${isInitializing || isPaused ? 'text-amber-600' : 'text-primary'}`}
                            >
                              {(isDictating || isInitializing || isPaused) && (
                                <div
                                  className={`h-2 w-2 rounded-full ${isInitializing || isPaused ? 'bg-amber-500' : 'animate-pulse bg-primary'}`}
                                />
                              )}
                              {isInitializing
                                ? 'Tengist...'
                                : isDictating
                                  ? insertionMode === 'insert'
                                    ? 'Setur inn við bendil...'
                                    : insertionMode === 'replace'
                                      ? 'Skiptir út vali...'
                                      : 'Hlustar...'
                                  : isPaused
                                    ? 'Í bið'
                                    : 'Diktering'}
                            </div>
                            {/* Finalization indicator - shown when stopping but no text received yet */}
                            {isFinalizingTranscription && pendingRefinements.length === 0 && !isSmartReviewing && (
                              <div className="flex items-center gap-1.5 text-slate-500 text-sm">
                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                <span>Lýk við umritun...</span>
                              </div>
                            )}
                            {/* Smart review indicator */}
                            {isSmartReviewing && (
                              <div className="flex items-center gap-1.5 text-primary text-sm">
                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                <span>Les yfir talgreiningu...</span>
                              </div>
                            )}
                          </div>
                          {/* Save transcript button - shown when modified */}
                          {isTranscriptModified && !isDictating && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={saveTranscript}
                              disabled={isSavingTranscript}
                              className="h-8 border-primary/30 px-3 text-primary text-sm hover:bg-primary/5"
                            >
                              {isSavingTranscript ? (
                                <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                              ) : (
                                <Save className="mr-1.5 h-4 w-4" />
                              )}
                              Vista umritun
                            </Button>
                          )}
                        </div>
                        <EditableTranscription
                          value={dictationText}
                          onChange={handleTranscriptChange}
                          provisionalText={provisionalText}
                          isRecording={isDictating}
                          onCursorPositionChange={(pos) => {
                            cursorPositionRef.current = pos;
                          }}
                          onSelectionChange={(range) => {
                            selectionRangeRef.current = range;
                          }}
                          targetCursorPosition={targetCursorPosition}
                          pendingRefinements={pendingRefinements}
                          editorRef={editorRef}
                        />
                      </div>
                    </TutorialAnchor>
                  </div>
                </ScrollArea>
              )}
            </div>

            {/* Mobile Bottom Nav */}
            <MobileSessionNavigation activeView={mobileView} onViewChange={setMobileView} />

            <SessionBottomBar
              languages={languages}
              transcriptionLanguage={selectedLanguage}
              summaryLanguage={selectedSummaryLanguage}
              onTranscriptionLanguageChange={setSelectedLanguage}
              onSummaryLanguageChange={setSelectedSummaryLanguage}
              languageDisabled={isDictating}
              recordedAudioUrl={recordedAudioUrl}
              onAudioDurationLoaded={(duration) => {
                if (isResumedSession && elapsedTime === 0 && duration > 0) {
                  setElapsedTime(Math.round(duration));
                }
              }}
              downloadFilename={`recording_${Date.now()}.wav`}
              audioUpload={
                <AudioUpload
                  mode="dictation"
                  language={selectedLanguage}
                  disabled={isDictating}
                  enableRuleBasedNormalization={enableRuleBasedNormalization}
                  ensureSession={ensureSession}
                  onUploadActiveChange={setUploading}
                  onTranscriptComplete={(result) => {
                    // Set text immediately for display
                    setDictationText((prev) => {
                      const separator = prev.trim() ? '\n\n' : '';
                      const newText = prev + separator + result.text;
                      cursorPositionRef.current = newText.length;
                      return newText;
                    });
                    if (result.file) {
                      if (recordedAudioUrl?.startsWith('blob:')) {
                        URL.revokeObjectURL(recordedAudioUrl);
                      }
                      setRecordedAudioUrl(URL.createObjectURL(result.file));
                    }

                    // The session was already ensured (and the transcript persisted
                    // under its id) by AudioUpload before the upload — nothing else
                    // to do here.
                  }}
                />
              }
            />
          </div>

          {/* Resize Handle - hidden on mobile */}
          <div className={layoutClasses.resizer} onMouseDown={handleMouseDown}>
            <div className="-left-1 -right-1 absolute inset-y-0 group-hover:bg-primary/10" />
          </div>

          {/* Right Panel: Smart Assistant - desktop only */}
          <div className={layoutClasses.panelColumn} style={{ width: `${rightPanelWidth}px` }}>
            <SmartPanel
              summary={summary}
              documents={documents}
              isProcessing={isProcessing}
              dictationMode={true}
              onToggleDictation={() => {}}
              dictationText={dictationText}
              onDictationChange={setDictationText}
              transcript={dictationText}
              onGenerateSummary={handleGenerateSummaryClick}
              isGeneratingSummary={isGeneratingSummary}
              isTranscriptionActive={isRecording && !isPaused}
              conversationTemplateId={selectedTemplateId}
              onConversationTemplateChange={handleTemplateChange}
              editedSummary={editedSummary}
              onSummaryEdit={handleSummaryEdit}
              onSummarySave={handleSummarySave}
              onCopySummary={handleSummaryCopy}
              noteInstructions={noteInstructions}
              isSavingSummary={isSavingSummary}
              sessionId={sessionIdRef.current}
              patient={bloodTestPatient}
              tasks={tasks}
              confirmedTasks={confirmedTasks}
              onToggleTaskCompletion={toggleCompletion}
              onTaskContentSaved={updateTaskContent}
              onContextChange={handlePreEncounterContextChange}
            />
          </div>

          {/* Mobile Smart Assistant - shown when assistant view is selected */}
          {mobileView === 'assistant' && (
            <MobileAssistantShell onViewChange={setMobileView}>
              <SmartPanel
                summary={summary}
                documents={documents}
                isProcessing={isProcessing}
                dictationMode={true}
                onToggleDictation={() => {}}
                dictationText={dictationText}
                onDictationChange={setDictationText}
                transcript={dictationText}
                onGenerateSummary={handleGenerateSummaryClick}
                isGeneratingSummary={isGeneratingSummary}
                isTranscriptionActive={isRecording && !isPaused}
                conversationTemplateId={selectedTemplateId}
                onConversationTemplateChange={handleTemplateChange}
                editedSummary={editedSummary}
                onSummaryEdit={handleSummaryEdit}
                onSummarySave={handleSummarySave}
                onCopySummary={handleSummaryCopy}
                noteInstructions={noteInstructions}
                isSavingSummary={isSavingSummary}
                sessionId={sessionIdRef.current}
                patient={bloodTestPatient}
                tasks={tasks}
                confirmedTasks={confirmedTasks}
                onToggleTaskCompletion={toggleCompletion}
                onTaskContentSaved={updateTaskContent}
                onContextChange={handlePreEncounterContextChange}
              />
            </MobileAssistantShell>
          )}
        </main>
      </div>
    </SessionViewport>
  );
}
